import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material";
import { Search, CheckCircle, Cancel } from "@mui/icons-material";
import { motion } from "framer-motion";

import {
  fetchTransactions,
  approveTransaction,
  rejectTransaction,
} from "../utils/api";

export default function TransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Fetch transactions
  const loadTransactions = async () => {
    setLoading(true);
    try {
      const data = await fetchTransactions({
        page: page + 1,
        per_page: rowsPerPage,
        status: statusFilter !== "All" ? statusFilter : null,
        type: typeFilter !== "All" ? typeFilter : null,
        search: search || null,
      });
      setTransactions(data.data);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadTransactions();
  }, [page, rowsPerPage, statusFilter, typeFilter]);

  // Approve/Reject Transaction
  const updateStatus = async (id, action) => {
    try {
      if (action === "approve") {
        await approveTransaction(id);
      } else {
        await rejectTransaction(id);
      }
      loadTransactions(); // refresh after update
      window.dispatchEvent(
        new CustomEvent("showSnackbar", {
          detail: {
            message: `Transaction ${action}`,
            severity: action === "approve" ? "success" : "error",
          },
        })
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box
  sx={{
    display: "flex",
    flexWrap: "wrap", // allow wrapping on smaller screens
    gap: 2,           // spacing between items
    mb: 2,
    alignItems: "center",
  }}
>
  <Typography
    variant="h5"
    sx={{ color: "#0ff", fontWeight: "bold", minWidth: "150px" }}
  >
    Transactions
  </Typography>

  <TextField
    size="small"
    placeholder="Search by user..."
    value={search}
    onChange={(e) => setSearch(e.target.value)}
    onKeyDown={(e) => e.key === "Enter" && loadTransactions()}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <Search sx={{ color: "#aaa" }} />
        </InputAdornment>
      ),
    }}
    sx={{
      flex: { xs: "1 1 100%", sm: "1 1 200px" }, // responsive width
      bgcolor: "rgba(25,25,50,0.7)",
      input: { color: "#fff" },
      borderRadius: 2,
      minWidth: 120,
    }}
  />

  <Select
    value={statusFilter}
    onChange={(e) => setStatusFilter(e.target.value)}
    size="small"
    sx={{
      flex: { xs: "1 1 100%", sm: "0 0 150px" }, // responsive width
      bgcolor: "rgba(4,4,7,0.7)",
      color: "#fff",
      borderRadius: 2,
      minWidth: 120,
    }}
  >
    <MenuItem value="All">All Status</MenuItem>
    <MenuItem value="pending">Pending</MenuItem>
    <MenuItem value="approved">Approved</MenuItem>
    <MenuItem value="rejected">Rejected</MenuItem>
  </Select>

  <Select
    value={typeFilter}
    onChange={(e) => setTypeFilter(e.target.value)}
    size="small"
    sx={{
      flex: { xs: "1 1 100%", sm: "0 0 150px" }, // responsive width
      bgcolor: "rgba(25,25,50,0.7)",
      color: "#fff",
      borderRadius: 2,
      minWidth: 120,
    }}
  >
    <MenuItem value="All">All Types</MenuItem>
    <MenuItem value="deposit">Deposit</MenuItem>
    <MenuItem value="withdrawal">Withdrawal</MenuItem>
    <MenuItem value="transfer">Transfer</MenuItem>
  </Select>
</Box>

      {/* Table */}
      <Card
        sx={{
          bgcolor: "rgba(25,25,50,0.7)",
          border: "1px solid #0ff4",
          borderRadius: 3,
          backdropFilter: "blur(12px)",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((tx, index) => (
                    <motion.tr
                      key={tx.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <TableCell>{tx.user?.name || `#${tx.user_id}`}</TableCell>
                      <TableCell>{tx.type}</TableCell>
                      <TableCell align="right">${tx.amount.toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip
                          label={tx.status}
                          sx={{
                            color: "#fff",
                            bgcolor:
                              tx.status === "pending"
                                ? "orange"
                                : tx.status === "approved"
                                ? "green"
                                : "red",
                            boxShadow:
                              tx.status === "pending"
                                ? "0 0 8px orange"
                                : tx.status === "approved"
                                ? "0 0 8px lime"
                                : "0 0 8px red",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(tx.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="center">
                        {tx.status === "pending" && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                color="success"
                                onClick={() => updateStatus(tx.id, "approve")}
                              >
                                <CheckCircle />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                color="error"
                                onClick={() => updateStatus(tx.id, "reject")}
                              >
                                <Cancel />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </motion.tr>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Card>
    </Box>
  );
}    